import { createGlobalStyle, css } from 'styled-components';
import { colors } from './colors';
import { spacings } from './spacings';
import { transitions } from './transitions';
import { elevations } from './elevations';
import { overlays } from './overlays';
import { radius } from './radius';

import DMSans from '../fonts/DMSans-Regular.ttf';
import Aeonik from '../fonts/Aeonik-Regular.otf';

const globalStyleFonts = css`
  @font-face {
    font-family: 'Aeonik';
    src: url('${Aeonik}') format('opentype');
  }
  @font-face {
    font-family: 'DM Sans';
    src: url('${DMSans}') format('opentype');
  }
`;

const globalStyleRadius = css`
  --radius-4: ${radius['4']};
  --radius-8: ${radius['8']};
  --radius-16: ${radius['16']};
  --radius-24: ${radius['24']};
  --radius-48: ${radius['48']};
  --radius-100: ${radius['100']};
  --radius-full: ${radius.full};
`;

const globalStyleOverlay = css`
  --overlay-light-75: ${overlays.light['75']};
  --overlay-light-50: ${overlays.light['50']};
  --overlay-light-25: ${overlays.light['25']};
  --overlay-light-10: ${overlays.light['10']};
  --overlay-light-5: ${overlays.light['5']};
  --overlay-dark-75: ${overlays.dark['75']};
  --overlay-dark-50: ${overlays.dark['50']};
  --overlay-dark-25: ${overlays.dark['25']};
  --overlay-dark-10: ${overlays.dark['10']};
  --overlay-dark-5: ${overlays.dark['5']};
`;

const globalStyleElevation = css`
  --elevation-soft: ${elevations.soft};
  --elevation-medium: ${elevations.medium};
  --elevation-hard: ${elevations.hard};
`;

const globalStyleTransitions = css`
  --transition-fast: ${transitions.fast};
  --transition-normal: ${transitions.normal};
`;

const globalStyleSpacings = css`
  --spacing-xxs: ${spacings.xxs};
  --spacing-xs: ${spacings.xs};
  --spacing-s: ${spacings.s};
  --spacing-m: ${spacings.m};
  --spacing-l: ${spacings.l};
  --spacing-xl: ${spacings.xl};
  --spacing-xxl: ${spacings.xxl};
`;

const globalStyleColors = css`
  --color-interface-primary-lightest: ${colors.interface.primary.lightest};
  --color-interface-primary-lighter: ${colors.interface.primary.lighter};
  --color-interface-primary-normal: ${colors.interface.primary.normal};
  --color-interface-primary-darker: ${colors.interface.primary.darker};
  --color-interface-primary-darkest: ${colors.interface.primary.darkest};

  --color-interface-secondary-lightest: ${colors.interface.secondary.lightest};
  --color-interface-secondary-lighter: ${colors.interface.secondary.lighter};
  --color-interface-secondary-normal: ${colors.interface.secondary.normal};
  --color-interface-secondary-darker: ${colors.interface.secondary.darker};
  --color-interface-secondary-darkest: ${colors.interface.secondary.darkest};

  --color-interface-layout-lightest: ${colors.interface.layout.lightest};
  --color-interface-layout-lighter: ${colors.interface.layout.lighter};
  --color-interface-layout-normal: ${colors.interface.layout.normal};
  --color-interface-layout-darker: ${colors.interface.layout.darker};
  --color-interface-layout-darkest: ${colors.interface.layout.darker};

  --color-interface-content-lightest: ${colors.interface.content.lightest};
  --color-interface-content-lighter: ${colors.interface.content.lighter};
  --color-interface-content-normal: ${colors.interface.content.normal};
  --color-interface-content-darker: ${colors.interface.content.darker};
  --color-interface-content-darkest: ${colors.interface.content.darkest};

  --color-interface-neutral-light: ${colors.interface.neutral.light};
  --color-interface-neutral-dark: ${colors.interface.neutral.dark};

  --color-feedback-success-lightest: ${colors.feedback.success.lightest};
  --color-feedback-success-lighter: ${colors.feedback.success.lighter};
  --color-feedback-success-normal: ${colors.feedback.success.normal};
  --color-feedback-success-darker: ${colors.feedback.success.darker};
  --color-feedback-success-darkest: ${colors.feedback.success.darkest};

  --color-feedback-warning-lightest: ${colors.feedback.warning.lightest};
  --color-feedback-warning-lighter: ${colors.feedback.warning.lighter};
  --color-feedback-warning-normal: ${colors.feedback.warning.normal};
  --color-feedback-warning-darker: ${colors.feedback.warning.darker};
  --color-feedback-warning-darkest: ${colors.feedback.warning.darkest};

  --color-feedback-error-lightest: ${colors.feedback.error.lightest};
  --color-feedback-error-lighter: ${colors.feedback.error.lighter};
  --color-feedback-error-normal: ${colors.feedback.error.normal};
  --color-feedback-error-darker: ${colors.feedback.error.darker};
  --color-feedback-error-darkest: ${colors.feedback.error.darkest};

  --color-feedback-information-lightest: ${colors.feedback.information
    .lightest};
  --color-feedback-information-lighter: ${colors.feedback.information.lighter};
  --color-feedback-information-normal: ${colors.feedback.information.normal};
  --color-feedback-information-darker: ${colors.feedback.information.darker};
  --color-feedback-information-darkest: ${colors.feedback.information.darkest};

  --color-brand-green1-lightest: ${colors.brand.green1.lightest};
  --color-brand-green1-lighter: ${colors.brand.green1.lighter};
  --color-brand-green1-normal: ${colors.brand.green1.normal};
  --color-brand-green1-darker: ${colors.brand.green1.darker};
  --color-brand-green1-darkest: ${colors.brand.green1.darkest};

  --color-brand-orange1-lightest: ${colors.brand.orange1.lightest};
  --color-brand-orange1-lighter: ${colors.brand.orange1.lighter};
  --color-brand-orange1-normal: ${colors.brand.orange1.normal};
  --color-brand-orange1-darker: ${colors.brand.orange1.darker};
  --color-brand-orange1-darkest: ${colors.brand.orange1.darkest};

  --color-brand-red1-lightest: ${colors.brand.red1.lightest};
  --color-brand-red1-lighter: ${colors.brand.red1.lighter};
  --color-brand-red1-normal: ${colors.brand.red1.normal};
  --color-brand-red1-darker: ${colors.brand.red1.darker};
  --color-brand-red1-darkest: ${colors.brand.red1.darkest};

  --color-brand-blue1-lightest: ${colors.brand.blue1.lightest};
  --color-brand-blue1-lighter: ${colors.brand.blue1.lighter};
  --color-brand-blue1-normal: ${colors.brand.blue1.normal};
  --color-brand-blue1-darker: ${colors.brand.blue1.darker};
  --color-brand-blue1-darkest: ${colors.brand.blue1.darkest};

  --color-brand-purple1-lightest: ${colors.brand.purple1.lightest};
  --color-brand-purple1-lighter: ${colors.brand.purple1.lighter};
  --color-brand-purple1-normal: ${colors.brand.purple1.normal};
  --color-brand-purple1-darker: ${colors.brand.purple1.darker};
  --color-brand-purple1-darkest: ${colors.brand.purple1.darkest};

  --color-brand-green2-lightest: ${colors.brand.green2.lightest};
  --color-brand-green2-lighter: ${colors.brand.green2.lighter};
  --color-brand-green2-normal: ${colors.brand.green2.normal};
  --color-brand-green2-darker: ${colors.brand.green2.darker};
  --color-brand-green2-darkest: ${colors.brand.green2.darkest};

  --color-brand-orange2-lightest: ${colors.brand.orange2.lightest};
  --color-brand-orange2-lighter: ${colors.brand.orange2.lighter};
  --color-brand-orange2-normal: ${colors.brand.orange2.normal};
  --color-brand-orange2-darker: ${colors.brand.orange2.darker};
  --color-brand-orange2-darkest: ${colors.brand.orange2.darkest};

  --color-brand-red2-lightest: ${colors.brand.red2.lightest};
  --color-brand-red2-lighter: ${colors.brand.red2.lighter};
  --color-brand-red2-normal: ${colors.brand.red2.normal};
  --color-brand-red2-darker: ${colors.brand.red2.darker};
  --color-brand-red2-darkest: ${colors.brand.red2.darkest};

  --color-brand-blue2-lightest: ${colors.brand.blue2.lightest};
  --color-brand-blue2-lighter: ${colors.brand.blue2.lighter};
  --color-brand-blue2-normal: ${colors.brand.blue2.normal};
  --color-brand-blue2-darker: ${colors.brand.blue2.darker};
  --color-brand-blue2-darkest: ${colors.brand.blue2.darkest};

  --color-brand-purple2-lightest: ${colors.brand.purple2.lightest};
  --color-brand-purple2-lighter: ${colors.brand.purple2.lighter};
  --color-brand-purple2-normal: ${colors.brand.purple2.normal};
  --color-brand-purple2-darker: ${colors.brand.purple2.darker};
  --color-brand-purple2-darkest: ${colors.brand.purple2.darkest};
`;

export const GlobalStyle = createGlobalStyle`
  :root {
    ${globalStyleFonts}
    ${globalStyleRadius}
    ${globalStyleOverlay}
    ${globalStyleElevation}
    ${globalStyleTransitions}
    ${globalStyleSpacings}
    ${globalStyleColors}
  }
`;
